import React, { useState, useEffect } from 'react';
import { Camera, Upload, Sparkles, Loader2, Image as ImageIcon, Video } from 'lucide-react';
import AppHeader from '../shared/AppHeader';
import { apiClient } from '../../utils/apiClient';

export default function LumaQuickMode({ currentUser, onLogout }) {
  const [mode, setMode] = useState('image');  // 'image' oder 'video'
  const [referenceImages, setReferenceImages] = useState([]);
  const [referenceImagePreviews, setReferenceImagePreviews] = useState([]);
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generations, setGenerations] = useState([]);
  
  // Image Settings (minimal)
  const [numImages, setNumImages] = useState(2);  // Default 2
  
  // Video Settings (nur essential)
  const [videoResolution, setVideoResolution] = useState('720p');
  const [videoDuration, setVideoDuration] = useState('5s');
  const [videoModel, setVideoModel] = useState('ray-2');
  const [videoLoop, setVideoLoop] = useState(false);
  
  useEffect(() => {
    const token = localStorage.getItem('snapshot_token');
    if (!token) {
      window.location.href = '/welcome';
      return;
    }
    
    // ADMIN CHECK!
    if (currentUser && currentUser.id !== '00000000-0000-0000-0000-000000000001') {
      alert('⚠️ Nur Admin hat Zugriff auf Luma Quick Mode!');
      window.location.href = '/dashboard';
      return;
    }
    
    loadGenerations();
  }, [currentUser]);
  
  const loadGenerations = async () => {
    try {
      const res = await apiClient.get('/api/luma/list');
      if (!res.ok) return;
      const data = await res.json();
      if (data.success) {
        setGenerations(data.generations || []);
      }
    } catch (error) {
      console.error('Galerie Error:', error);
    }
  };
  
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 4) {
      alert('Max 4 Bilder!');
      return;
    }
    
    if (files.length === 0) return;
    
    setReferenceImages(files);
    
    // Create previews synchronously
    const previews = [];
    let loadedCount = 0;
    
    files.forEach((file, index) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        previews[index] = event.target.result;
        loadedCount++;
        
        // Update previews when all are loaded
        if (loadedCount === files.length) {
          setReferenceImagePreviews(previews);
          console.log(`✅ ${files.length} Bilder geladen`);
        }
      };
      reader.readAsDataURL(file);
    });
  };
  
  const handleGenerate = async () => {
    if (referenceImages.length === 0) {
      alert('Bitte 1-4 Fotos hochladen!');
      return;
    }
    
    setIsGenerating(true);
    
    try {
      const formData = new FormData();
      
      referenceImages.forEach((img) => {
        formData.append('reference_images', img);
      });
      
      formData.append('prompt', prompt);
      
      const token = localStorage.getItem('snapshot_token');
      const endpoint = mode === 'image' ? '/api/luma/generate/image' : '/api/luma/generate/video';
      
      if (mode === 'image') {
        formData.append('model', 'photon-1');  // Fixiert auf photon-1
        formData.append('image_format', 'portrait_9_16');  // Fixiert auf Portrait
        formData.append('num_images', numImages.toString());
      } else {
        formData.append('resolution', videoResolution);
        formData.append('duration', videoDuration);
        formData.append('aspect_ratio', '9:16');  // Fixiert auf Portrait
        formData.append('model', videoModel);
        formData.append('loop', videoLoop.toString());
      }
      
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!res.ok) {
        const error = await res.text();
        throw new Error(error || `Error ${res.status}`);
      }
      
      const data = await res.json();
      console.log('✅ Success:', data);
      
      await loadGenerations();
      setPrompt('');
      
      alert(`✅ ${mode === 'image' ? 'Bilder' : 'Video'} generiert!`);
      
    } catch (error) {
      console.error('❌ Error:', error);
      alert(`Fehler: ${error.message}`);
    } finally {
      setIsGenerating(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-black">
      <AppHeader currentUser={currentUser} onLogout={onLogout} />
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">
            ⚡ Luma Quick Mode
          </h1>
          <p className="text-white/70">
            Bilder & Videos mit 100% Face Match
          </p>
          <p className="text-[#10B981] text-sm mt-2">
            🔒 Nur Admin
          </p>
        </div>
        
        {/* Mode Toggle */}
        <div className="flex gap-4 mb-8 justify-center">
          <button
            onClick={() => setMode('image')}
            className={`px-6 py-3 rounded-xl font-bold flex items-center gap-2 ${
              mode === 'image'
                ? 'bg-[#10B981] text-black'
                : 'bg-white/10 text-white/70 hover:bg-white/20'
            }`}
          >
            <ImageIcon className="w-5 h-5" />
            Bild
          </button>
          <button
            onClick={() => setMode('video')}
            className={`px-6 py-3 rounded-xl font-bold flex items-center gap-2 ${
              mode === 'video'
                ? 'bg-[#10B981] text-black'
                : 'bg-white/10 text-white/70 hover:bg-white/20'
            }`}
          >
            <Video className="w-5 h-5" />
            Video
          </button>
        </div>
        
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-8">
          
          {/* Upload */}
          <div className="mb-6">
            <label className="block text-lg font-bold text-white mb-2">
              1. Fotos (1-4 = besseres Face Match!)
            </label>
            
            {referenceImagePreviews.length === 0 ? (
              <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-white/30 rounded-xl cursor-pointer hover:border-[#10B981]/50 bg-black/30">
                <Upload className="w-12 h-12 text-white/50 mb-3" />
                <span className="text-white/70">1-4 Fotos hochladen</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>
            ) : (
              <div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  {referenceImagePreviews.map((preview, idx) => (
                    <img key={idx} src={preview} alt={`Ref ${idx+1}`} className="w-full h-40 object-cover rounded-lg" />
                  ))}
                </div>
                <button onClick={() => { setReferenceImages([]); setReferenceImagePreviews([]); }} className="w-full px-3 py-2 bg-red-500/80 text-white rounded-lg">
                  Entfernen
                </button>
              </div>
            )}
          </div>
          
          {/* Prompt */}
          <div className="mb-6">
            <label className="block text-lg font-bold text-white mb-2">2. Szene beschreiben</label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="auf einer luxuriösen Rooftop-Party in Monaco, mit Champagnerglas, goldenes Abendlicht..."
              className="w-full px-4 py-3 bg-black border-2 border-white/30 rounded-xl text-white placeholder:text-white/40 focus:border-[#10B981] resize-none"
              rows={4}
            />
            <p className="text-white/50 text-xs mt-2">
              💡 Tipp: Beschreibe die Szene detailliert. Das System fügt automatisch hinzu: "die gleiche junge Frau aus dem Referenzfoto..."
            </p>
          </div>
          
          <button
            onClick={handleGenerate}
            disabled={isGenerating || referenceImages.length === 0 || !prompt}
            className={`w-full py-4 mt-6 rounded-xl font-bold text-lg flex items-center justify-center gap-3 ${
              isGenerating || referenceImages.length === 0 || !prompt
                ? 'bg-white/10 text-white/40'
                : 'bg-[#10B981] text-black hover:bg-[#059669]'
            }`}
          >
            {isGenerating ? <><Loader2 className="w-5 h-5 animate-spin" /> {mode === 'image' ? `Generiere ${numImages}...` : 'Video...'}</> : <><Sparkles className="w-5 h-5" /> {mode === 'image' ? `${numImages} Bilder` : 'Video'}</>}
          </button>
          
        </div>
        
        {/* Gallery */}
        <div>
          <h2 className="text-2xl font-bold text-white mb-4">Galerie</h2>
          {generations.length === 0 ? (
            <div className="text-center py-12 text-white/50">Keine Generationen</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {generations.map((gen) => {
                const imgs = Array.isArray(gen.image_urls) ? gen.image_urls : [];
                const vid = gen.video_url;
                const isVid = gen.generation_type === 'video';
                
                return (
                  <div key={gen.id} className="bg-white/5 border border-white/10 rounded-xl p-4">
                    {isVid ? (
                      vid && <video src={vid} controls loop={gen.generation_settings?.loop} className="w-full rounded-lg mb-3" />
                    ) : (
                      imgs.length > 0 && (
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          {imgs.map((url, i) => <img key={i} src={url} alt={`${i+1}`} className="w-full rounded-lg" />)}
                        </div>
                      )
                    )}
                    
                    {/* User Prompt */}
                    <div className="mb-2">
                      <p className="text-white/50 text-xs font-bold">User Prompt:</p>
                      <p className="text-white/80 text-sm">"{gen.prompt}"</p>
                    </div>
                    
                    {/* Prompt-Anzeige (User sieht nur seinen eigenen Prompt) */}
                    {gen.prompt_text && (
                      <div className="mb-2 p-2 bg-white/5 rounded">
                        <p className="text-[#10B981] text-xs font-bold">Dein Prompt:</p>
                        <p className="text-white/70 text-xs">{gen.prompt_text}</p>
                      </div>
                    )}
                    
                    <p className="text-white/50 text-xs mt-2">{isVid ? '🎬' : '🖼️'} {gen.created_at ? new Date(gen.created_at).toLocaleString('de-DE') : ''}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
